import * as z from "zod";

export const artifactRelativePathSchema = z
  .string()
  .min(1)
  .max(2048)
  .refine((path) => {
    return (
      !path.startsWith("/") &&
      !path.includes("\\") &&
      !path.includes("%") &&
      !path.includes("\0") &&
      path
        .split("/")
        .every(
          (segment) => segment !== "" && segment !== "." && segment !== "..",
        )
    );
  });
/** Lattice's ARTIFACT_STYLES names. Schemas can't import lattice; a test keeps the lists in sync. */
export const artifactStyleSchema = z.enum([
  "default",
  "editorial",
  "newsprint",
  "magazine",
  "analyst",
  "terminal",
  "soft",
  "bold",
]);

/** Lattice's ARTIFACT_ACCENTS names; a test keeps the lists in sync. */
export const artifactAccentSchema = z.enum([
  "red",
  "orange",
  "green",
  "teal",
  "blue",
  "purple",
]);

export const artifactReportManifestSchema = z.looseObject({
  version: z.literal(1),
  description: z.string().trim().min(1).max(1000).optional(),
  // Loose like style; the write check and the runtime enforce artifactThemeSchema.
  theme: z
    .record(z.string(), z.unknown())
    .optional()
    .describe(
      "The artifact's colours, copied from the creator's theme when the artifact is made: { mode, light, dark }, where light and dark map CSS variables to values. Omitted means the runtime's default colours.",
    ),
  // Any short string, so an older reader never rejects (or rewrites away) a style added later.
  style: z
    .string()
    .max(64)
    .optional()
    .describe(
      "The artifact's style: one of default, editorial, newsprint, magazine, analyst, terminal, soft, bold. Copied from the creator's theme when the artifact is made; omitted means no style is applied.",
    ),
});
export const ARTIFACT_MAX_FILES = 100;
export const ARTIFACT_MAX_CONTENT_CHARS = 8_000_000;
export const artifactTemplateSchema = z
  .object({
    tags: z.array(z.string().trim().min(1).max(64)).max(30),
    files: z
      .array(
        z.object({
          path: artifactRelativePathSchema,
          contentType: z.string().max(255),
          encoding: z.enum(["utf8", "base64"]),
          content: z.string().max(ARTIFACT_MAX_CONTENT_CHARS),
        }),
      )
      .min(1)
      .max(ARTIFACT_MAX_FILES),
  })
  .refine((template) => {
    return (
      template.files.some((file) => file.path === "index.html") &&
      new Set(template.files.map((file) => file.path)).size ===
        template.files.length &&
      template.files.reduce((total, file) => total + file.content.length, 0) <=
        ARTIFACT_MAX_CONTENT_CHARS
    );
  }, "A recipe needs index.html, unique relative files, and at most 8 MB of content.");
export type ArtifactTemplate = z.infer<typeof artifactTemplateSchema>;

export function normalizeArtifactTags(tags: string[]): string[] {
  return [
    ...new Set(tags.map((tag) => tag.trim().toLowerCase()).filter(Boolean)),
  ];
}

export const ARTIFACT_INDEX_FILE_NAME = "index.html";
export const ARTIFACT_MANIFEST_FILE_NAME = "artifact.json";
/** An Insights report artifact keeps its document under this name, beside the `index.html` that renders it. */
export const ARTIFACT_REPORT_FILE_NAME = "report.insights.json";

export function artifactFolderName(title: string): string {
  return `${title.replace(/[/\\]/g, "-").slice(0, 210)}.artifact`;
}

export function groupInsightsArtifacts<ArtifactType extends { tags: string[] }>(
  artifacts: ArtifactType[],
): { tag: string; artifacts: ArtifactType[] }[] {
  const groups = new Map<string, ArtifactType[]>();
  for (const artifact of artifacts) {
    const tags = normalizeArtifactTags(artifact.tags);
    if (!tags.includes("insights")) {
      continue;
    }
    const topics = tags.filter((tag) => tag !== "insights");
    for (const tag of topics.length ? topics : [""]) {
      const group = groups.get(tag) ?? [];
      group.push(artifact);
      groups.set(tag, group);
    }
  }
  return [...groups]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([tag, artifacts]) => ({ tag, artifacts }));
}
